import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs/Subscription';
import { TranslateService } from '@ngx-translate/core';
import { FormGroup } from '@angular/forms';

import { User, UserId } from '../../user.model';
import { UserRepository } from '../../user.repository';
import { ROLE } from '../../roles.const';
import { QueryParams, PaginationList, noop, EMPTY_MESSAGE, FilterFormBuilder, InputFilter } from 'app/shared';
import { NotifyService, ToastType } from 'app/notify';
import { UploaderService, UploaderOptions } from 'app/uploader';
import { environment } from 'environments/environment';

@Component({
  selector: 'app-user-list',
  templateUrl: './user-list.component.html',
  styleUrls: ['./user-list.component.scss'],
  providers: [
    QueryParams,
    FilterFormBuilder
  ]
})
export class UserListComponent implements OnInit, OnDestroy {

  @Input()
  public role: string = null;
  public roleParams: string = null;
  public groupFilterForm: FormGroup = null;
  public userList: PaginationList<User> = new PaginationList<User>();
  public emptyMessageKey = EMPTY_MESSAGE.NOT_FOUND;

  private gridSubscription: Subscription = null;
  private uploaderSubscription: Subscription = null;

  constructor(
    private router: Router,
    private userRepository: UserRepository,
    private notify: NotifyService,
    public queryParams: QueryParams,
    private translateService: TranslateService,
    private filterFormBuilder: FilterFormBuilder,
    private uploaderService: UploaderService
  ) {
    this.gridSubscription = this.queryParams.onChange.subscribe(() => this.reloadUsersList());
  }

  ngOnInit() {
    this.reloadUsersList();
    this.createForm();

    if (this.queryParams.getParam('filters[role]')) {
      this.roleParams = this.queryParams.getParam('filters[role]');
    }
  }

  ngOnDestroy() {
    if (null !== this.uploaderSubscription) {
      this.uploaderSubscription.unsubscribe();
    }

    if (null !== this.gridSubscription) {
      this.gridSubscription.unsubscribe();
    }
  }

  public reloadUsersList(): void {
    this.emptyMessageKey = EMPTY_MESSAGE.LOADING_DATA;
    this.queryParams.setParam('filters[role]', this.role);

    this.userRepository.list(
      this.queryParams.getURLSearchParams()
    )
    .subscribe(
      (userList: PaginationList<User>): void => {
        this.emptyMessageKey = EMPTY_MESSAGE.NOT_FOUND;
        this.userList = userList;
      },
      noop
    );
  }

  public loadUsersPage(page: number = 1): void {
    this.queryParams.setParam('page', page);
  }

  public confirmDeleteUser(user: User) {
    this.notify.confirm(
      this.translateService.instant(
        'user.confirm_deletion.message',
        {
          name: user.name,
          surname: user.surname
        }
      ),
      this.translateService.instant('user.confirm_deletion.title')
    )
    .subscribe(
      () => this.deleteUser(user.getId()),
      noop
    );
  }

  public assignDataToControlName({ value }, formControlName: string): void {
    this.groupFilterForm.get(formControlName).setValue(value);
  }

  private deleteUser(userId: UserId): void {
    this.userRepository.delete(userId)
    .subscribe(
      () => {
        this.reloadUsersList();
        this.notify.toast(ToastType.SUCCESS, this.translateService.instant('user.message.user_deleted'));
      },
      () => {
        this.notify.toast(ToastType.SUCCESS, this.translateService.instant('user.error.user_not_deleted'));
      }
    );
  }

  private createForm(): void {
    const options = { debounceTime: 1000, minLength: 1 };

    this.filterFormBuilder.createFilters([
      new InputFilter('email', options),
      new InputFilter('fullName', options),
      new InputFilter('company', options),
      new InputFilter('industry', options),
      new InputFilter('department', options)
    ]);

    this.groupFilterForm = this.filterFormBuilder.getFilterForm();
  }

  public openUploaderModal(): void {
    const acceptableFileFormat = '.xls,.xlsx';

    const uploaderOptions = new UploaderOptions(
      environment.apiUrl + 'file/excel',
      acceptableFileFormat
    );

    this.uploaderSubscription = this.uploaderService
      .openModal(uploaderOptions)
      .subscribe(
        (excelId: string) => {
          this.router.navigate(['/participant/import/', excelId]);
        },
        noop
      );
  }
}



// WEBPACK FOOTER //
// /dist/src/app/user/user-shared/user-list/user-list.component.ts